import EventEmitter from 'events';

/**
 * Logger utility - only logs in development mode
 * Keeps error and warn logging in all environments for debugging
 */
const logger = {
  log: (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[VoiceService]', ...args);
    }
  },
  error: (...args) => {
    // Always log errors
    console.error('[VoiceService ERROR]', ...args);
  },
  warn: (...args) => {
    // Always log warnings
    console.warn('[VoiceService WARN]', ...args);
  }
};
export class VoiceService extends EventEmitter {
  constructor(token) {
    super();
    this.token = token;
    this.ws = null;
    this.sessionId = `session-${Date.now()}`;
    this.mediaRecorder = null;
    this.audioContext = null;
    this.audioChunks = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isConnecting = false;
    this.isDisconnecting = false;
    
    // Streaming audio properties
    this.streamingAudioChunks = [];
    this.isStreamingAudio = false;
    this.streamingAudioSource = null;
    
    // Sentence-based audio queue for smooth real-time playback
    this.sentenceAudioQueue = [];
    this.isPlayingSentences = false;
    this.currentSentenceIndex = 0;
    
    // TTS audio queue
    this.ttsAudioQueue = [];
    this.isPlayingTTSAudio = false;
  }

  connect() {
    // Don't connect if already connecting, connected, or no token
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN) || !this.token) {
      return;
    }

    this.isConnecting = true;
    this.isDisconnecting = false; // Reset disconnecting flag
    
    const getWsBaseUrl = () => {
      if (process.env.REACT_APP_WS_URL) {
        return process.env.REACT_APP_WS_URL.replace(/\/$/, '');
      }

      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

      try {
        const parsed = new URL(apiBase);
        parsed.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
        return parsed.toString().replace(/\/$/, '');
      } catch (error) {
        logger.warn('⚠️ Invalid API base URL, falling back to default WS URL', error);
        return 'wss://asphalt-lca-calculator-357827890895.us-central1.run.app';
      }
    };

    const wsUrl = `${getWsBaseUrl()}/chatbot/ws/${this.sessionId}?token=${this.token}`;
    
    try {
      logger.log('🔌 Connecting to Voice Assistant WebSocket...');
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        logger.log('✅ Voice Assistant connected successfully');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emit('connected');
      };
      
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.emit('message', data);
        } catch (error) {
          logger.error('❌ Failed to parse WebSocket message:', error);
        }
      };
      
      this.ws.onerror = (error) => {
        logger.error('❌ WebSocket connection error:', error);
        this.isConnecting = false;
        
        // Only emit error if we're not in the middle of disconnecting
        if (!this.isDisconnecting) {
          this.emit('error', { type: 'connection', message: 'WebSocket connection failed' });
        }
      };
      
      this.ws.onclose = (event) => {
        logger.log('🔌 Voice Assistant WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        this.emit('disconnected');
        
        // Only attempt to reconnect if:
        // 1. Not intentionally disconnecting
        // 2. Haven't exceeded max attempts
        // 3. Close code indicates we should retry (not authentication failure)
        const shouldReconnect = !this.isDisconnecting && 
                               this.reconnectAttempts < this.maxReconnectAttempts &&
                               event.code !== 4001 && // Don't reconnect on auth failure
                               event.code !== 1000;   // Don't reconnect on normal closure
        
        if (shouldReconnect) {
          this.reconnectAttempts++;
          logger.log(`🔄 Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => this.connect(), this.reconnectDelay);
        } else if (event.code === 4001) {
          logger.error('❌ Authentication failed - Voice Assistant unavailable');
          this.emit('error', { type: 'auth', message: 'Authentication failed' });
        }
      };
    } catch (error) {
      logger.error('❌ Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      this.emit('error', { type: 'connection', message: 'Failed to create WebSocket' });
    }
  }

  disconnect() {
    this.isDisconnecting = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Reset sentence index for new messages
      if (message.type === 'text') {
        this.currentSentenceIndex = 0;
        this.sentenceAudioQueue = [];
      }
      this.ws.send(JSON.stringify(message));
    } else {
      logger.warn('WebSocket not connected, queuing message');
      // You could implement a message queue here
    }
  }

  async startListening() {
    try {
      // Use voice recognition API (built-in)
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        logger.warn('Speech Recognition not supported, falling back to audio recording');
        return this.startAudioRecording();
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.speechRecognition = new SpeechRecognition();
      
      // Configure speech recognition for better accuracy
      this.speechRecognition.continuous = false;          // Stop after one result
      this.speechRecognition.interimResults = true;       // Show interim results
      this.speechRecognition.lang = 'en-US';              // Set language
      this.speechRecognition.maxAlternatives = 1;         // Only best result

      let finalTranscript = '';
      let interimTranscript = '';

      this.speechRecognition.onstart = () => {
        logger.log('🎤 Voice recognition started');
        this.emit('listening', true);
      };

      this.speechRecognition.onresult = (event) => {
        interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Emit interim results for real-time feedback
        if (interimTranscript) {
          this.emit('interim_transcript', interimTranscript);
        }

        // Send final transcript when complete
        if (finalTranscript) {
          logger.log('🎤 Final transcript:', finalTranscript);
          
          // Immediately show user's question in the UI
          this.emit('user_message', {
            text: finalTranscript.trim(),
            timestamp: new Date().toLocaleTimeString(),
            source: 'voice'
          });
          
          this.send({
            type: 'text',
            text: finalTranscript.trim(),
            voice: true  // Indicate this came from voice input
          });
          finalTranscript = '';
        }
      };

      this.speechRecognition.onerror = (event) => {
        logger.error('Speech recognition error:', event.error);
        this.emit('listening', false);
        
        // Handle different error types
        switch (event.error) {
          case 'no-speech':
            this.emit('error', { type: 'no-speech', message: 'No speech detected. Please try again.' });
            break;
          case 'audio-capture':
            this.emit('error', { type: 'microphone', message: 'Microphone not accessible.' });
            break;
          case 'not-allowed':
            this.emit('error', { type: 'permission', message: 'Microphone permission denied.' });
            break;
          case 'network':
            this.emit('error', { type: 'network', message: 'Network error during speech recognition.' });
            break;
          default:
            this.emit('error', { type: 'recognition', message: `Speech recognition error: ${event.error}` });
        }
      };

      this.speechRecognition.onend = () => {
        logger.log('🎤 Voice recognition ended');
        this.emit('listening', false);
      };

      // Start recognition
      this.speechRecognition.start();
      return true;
      
    } catch (error) {
      logger.error('Error starting voice recognition:', error);
      this.emit('error', { type: 'recognition', message: 'Unable to start voice recognition' });
      return false;
    }
  }

  async startAudioRecording() {
    // Fallback to audio recording if Speech Recognition is not available
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      this.mediaRecorder = new MediaRecorder(stream);
      this.audioChunks = [];
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        const reader = new FileReader();
        
        reader.onloadend = () => {
          const base64Audio = reader.result.split(',')[1];
          this.send({
            type: 'audio',
            data: base64Audio
          });
        };
        
        reader.readAsDataURL(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      this.mediaRecorder.start();
      this.emit('listening', true);
      return true;
      
    } catch (error) {
      logger.error('Error accessing microphone:', error);
      this.emit('error', { type: 'microphone', message: 'Unable to access microphone' });
      return false;
    }
  }

  stopListening() {
    // Stop speech recognition if active
    if (this.speechRecognition) {
      try {
        this.speechRecognition.stop();
        this.speechRecognition = null;
        logger.log('🎤 Speech recognition stopped');
      } catch (error) {
        logger.warn('Error stopping speech recognition:', error);
      }
    }

    // Stop audio recording if active
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      logger.log('🎤 Audio recording stopped');
    }

    this.emit('listening', false);
  }

  async playAudio(base64Audio) {
    try {
      this.emit('speaking', true);
      
      const audioData = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      
      for (let i = 0; i < audioData.length; i++) {
        uint8Array[i] = audioData.charCodeAt(i);
      }
      
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      
      source.onended = () => {
        this.emit('speaking', false);
      };
      
      source.start();
      
    } catch (error) {
      logger.error('Error playing audio:', error);
      this.emit('speaking', false);
    }
  }

  startAudioStream() {
    this.streamingAudioChunks = [];
    this.isStreamingAudio = true;
    this.emit('speaking', true);
    logger.log('Started audio streaming');
  }

  addAudioChunk(base64Chunk) {
    if (!this.isStreamingAudio) return;
    
    try {
      // Convert base64 chunk to audio data
      const audioData = atob(base64Chunk);
      const uint8Array = new Uint8Array(audioData.length);
      
      for (let i = 0; i < audioData.length; i++) {
        uint8Array[i] = audioData.charCodeAt(i);
      }
      
      this.streamingAudioChunks.push(uint8Array);
      
      // If this is the first chunk, start playing immediately
      if (this.streamingAudioChunks.length === 1) {
        this.playStreamingAudio();
      }
      
    } catch (error) {
      logger.error('Error processing audio chunk:', error);
    }
  }

  async playStreamingAudio() {
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Combine all chunks received so far
      const totalLength = this.streamingAudioChunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combinedArray = new Uint8Array(totalLength);
      
      let offset = 0;
      for (const chunk of this.streamingAudioChunks) {
        combinedArray.set(chunk, offset);
        offset += chunk.length;
      }

      try {
        const audioBuffer = await this.audioContext.decodeAudioData(combinedArray.buffer.slice());
        
        // Stop previous streaming source if it exists
        if (this.streamingAudioSource) {
          this.streamingAudioSource.stop();
        }
        
        this.streamingAudioSource = this.audioContext.createBufferSource();
        this.streamingAudioSource.buffer = audioBuffer;
        this.streamingAudioSource.connect(this.audioContext.destination);
        
        this.streamingAudioSource.onended = () => {
          if (!this.isStreamingAudio) {
            this.emit('speaking', false);
          }
        };
        
        this.streamingAudioSource.start();
        
      } catch (decodeError) {
        // Audio not complete yet, wait for more chunks
        logger.log('Waiting for more audio chunks...');
      }
      
    } catch (error) {
      logger.error('Error playing streaming audio:', error);
    }
  }

  completeAudioStream() {
    this.isStreamingAudio = false;
    
    // Play the final complete audio
    if (this.streamingAudioChunks.length > 0) {
      this.playFinalStreamingAudio();
    } else {
      this.emit('speaking', false);
    }
    
    logger.log('Completed audio streaming');
  }

  async playFinalStreamingAudio() {
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Combine all chunks for final playback
      const totalLength = this.streamingAudioChunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combinedArray = new Uint8Array(totalLength);
      
      let offset = 0;
      for (const chunk of this.streamingAudioChunks) {
        combinedArray.set(chunk, offset);
        offset += chunk.length;
      }

      const audioBuffer = await this.audioContext.decodeAudioData(combinedArray.buffer.slice());
      
      // Stop any previous source
      if (this.streamingAudioSource) {
        this.streamingAudioSource.stop();
      }
      
      this.streamingAudioSource = this.audioContext.createBufferSource();
      this.streamingAudioSource.buffer = audioBuffer;
      this.streamingAudioSource.connect(this.audioContext.destination);
      
      this.streamingAudioSource.onended = () => {
        this.emit('speaking', false);
        this.streamingAudioSource = null;
      };
      
      this.streamingAudioSource.start();
      
    } catch (error) {
      logger.error('Error playing final streaming audio:', error);
      this.emit('speaking', false);
    }
  }

  handleAudioError() {
    this.isStreamingAudio = false;
    this.streamingAudioChunks = [];
    if (this.streamingAudioSource) {
      this.streamingAudioSource.stop();
      this.streamingAudioSource = null;
    }
    this.emit('speaking', false);
    logger.error('Audio streaming error occurred');
  }

  // Queue sentence audio for sequential playback
  queueSentenceAudio(base64Audio, format = 'mp3', sentenceIndex) {
    try {
      // Convert base64 to blob
      const audioData = atob(base64Audio);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      
      // Use correct MIME type for MP3
      const mimeType = format === 'mp3' ? 'audio/mpeg' : `audio/${format}`;
      const audioBlob = new Blob([audioArray], { type: mimeType });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Create audio element
      const audio = new Audio(audioUrl);
      audio.volume = 0.8;
      
      // Add to queue with index
      this.sentenceAudioQueue.push({
        audio: audio,
        index: sentenceIndex,
        url: audioUrl
      });
      
      logger.log(`📦 Queued sentence ${sentenceIndex} (${this.sentenceAudioQueue.length} in queue)`);
      
      // Start playing if not already playing
      if (!this.isPlayingSentences) {
        this.playNextSentence();
      }
      
    } catch (error) {
      logger.error('Error queueing sentence audio:', error);
    }
  }
  
  playNextSentence() {
    // Find the next sentence to play
    const nextSentence = this.sentenceAudioQueue.find(s => s.index === this.currentSentenceIndex);
    
    if (!nextSentence) {
      // No sentence ready yet, wait a bit
      if (this.sentenceAudioQueue.length > 0) {
        setTimeout(() => this.playNextSentence(), 100);
      } else {
        this.isPlayingSentences = false;
        this.emit('speaking', false);
      }
      return;
    }
    
    this.isPlayingSentences = true;
    this.emit('speaking', true);
    
    const { audio, url, index } = nextSentence;
    logger.log(`🔊 Playing sentence ${index}`);
    
    audio.onended = () => {
      URL.revokeObjectURL(url);
      // Remove played sentence from queue
      this.sentenceAudioQueue = this.sentenceAudioQueue.filter(s => s.index !== index);
      this.currentSentenceIndex++;
      // Play next sentence
      this.playNextSentence();
    };
    
    audio.onerror = (error) => {
      logger.error(`Error playing sentence ${index}:`, error);
      URL.revokeObjectURL(url);
      this.sentenceAudioQueue = this.sentenceAudioQueue.filter(s => s.index !== index);
      this.currentSentenceIndex++;
      this.playNextSentence();
    };
    
    audio.play().catch(error => {
      logger.error(`Failed to play sentence ${index}:`, error);
      this.currentSentenceIndex++;
      this.playNextSentence();
    });
  }
  
  processSentenceQueue() {
    // Called when all sentences have been sent
    logger.log('📚 Processing remaining sentence queue...');
    if (!this.isPlayingSentences && this.sentenceAudioQueue.length > 0) {
      this.playNextSentence();
    }
  }
  
  // Legacy method name for compatibility
  playEdgeTTSAudio(base64Audio, format) {
    return this.playTTSAudio(base64Audio, format);
  }
  
  playTTSAudio(base64Audio, format = 'mp3') {
    // Play TTS audio (supports MP3, WAV, etc.) - UPDATED FOR GOOGLE CLOUD TTS
    try {
      logger.log(`🎵 Playing TTS audio (${format})...`);
      this.emit('speaking', true);
      
      // Convert base64 to blob
      const audioData = atob(base64Audio);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      
      // Create blob with correct MIME type for MP3
      const mimeType = format === 'mp3' ? 'audio/mpeg' : `audio/${format}`;
      const audioBlob = new Blob([audioArray], { type: mimeType });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Create and play audio element
      const audio = new Audio(audioUrl);
      audio.volume = 0.8;
      
      // Queue for smooth playback
      if (!this.ttsAudioQueue) {
        this.ttsAudioQueue = [];
        this.isPlayingTTSAudio = false;
      }
      
      this.ttsAudioQueue.push({ audio, url: audioUrl });
      
      // Play queued audio sequentially
      if (!this.isPlayingTTSAudio) {
        this.playNextTTSAudio();
      }
      
    } catch (error) {
      logger.error('Error playing TTS audio:', error);
      this.emit('speaking', false);
    }
  }
  
  playNextTTSAudio() {
    if (!this.ttsAudioQueue || this.ttsAudioQueue.length === 0) {
      this.isPlayingTTSAudio = false;
      this.emit('speaking', false);
      return;
    }
    
    this.isPlayingTTSAudio = true;
    const { audio, url } = this.ttsAudioQueue.shift();
    
    audio.onended = () => {
      URL.revokeObjectURL(url);
      this.playNextTTSAudio();
    };
    
    audio.onerror = (error) => {
      logger.error('TTS audio playback error:', error);
      URL.revokeObjectURL(url);
      this.playNextTTSAudio();
    };
    
    audio.play().catch(error => {
      logger.error('Error starting audio playback:', error);
      this.playNextTTSAudio();
    });
  }

  playBrowserTTS(text, voiceSettings = {}) {
    // Use browser's built-in Speech Synthesis API (completely free)
    if (!('speechSynthesis' in window)) {
      logger.error('Speech Synthesis not supported');
      return;
    }

    try {
      // For parallel TTS, don't cancel existing speech - let them overlap briefly
      if (!voiceSettings.is_parallel) {
        speechSynthesis.cancel();
      }

      this.emit('speaking', true);

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Configure voice settings for more human-like speech
      utterance.rate = voiceSettings.rate || 0.95;   // Optimal speed for understanding
      utterance.pitch = voiceSettings.pitch || 1.0;  // Natural pitch
      utterance.volume = voiceSettings.volume || 0.8; // Comfortable volume

      // Try to find the best human-like voice
      const voices = speechSynthesis.getVoices();
      
      if (voices.length > 0) {
        // Priority list of preferred voices (more natural sounding)
        const preferredVoices = [
          // High quality voices (order matters)
          'Samantha', 'Karen', 'Susan', 'Victoria', 'Allison', 'Ava',
          // Google voices
          'Google US English Female', 'Google UK English Female',
          // Microsoft voices  
          'Microsoft Zira Desktop', 'Microsoft Hazel Desktop',
          'Microsoft Eva Desktop', 'Microsoft Aria Desktop',
          // Edge voices
          'Microsoft Aria Online', 'Microsoft Jenny Online',
          // Any female voice
          'female'
        ];

        let selectedVoice = null;

        // Try to find preferred voices in order
        for (const preferred of preferredVoices) {
          selectedVoice = voices.find(voice => 
            voice.name.toLowerCase().includes(preferred.toLowerCase())
          );
          if (selectedVoice) break;
        }

        // Fallback to any good English voice
        if (!selectedVoice) {
          selectedVoice = voices.find(voice => 
            voice.lang.startsWith('en') && 
            (voice.name.toLowerCase().includes('female') ||
             voice.name.toLowerCase().includes('woman') ||
             voice.name.toLowerCase().includes('aria') ||
             voice.name.toLowerCase().includes('jenny') ||
             !voice.name.toLowerCase().includes('male'))
          );
        }

        // Final fallback to any English voice
        if (!selectedVoice) {
          selectedVoice = voices.find(voice => voice.lang.startsWith('en'));
        }

        utterance.voice = selectedVoice || voices[0];
        
        if (voiceSettings.is_parallel) {
          logger.log(`🎵 Parallel TTS using: ${utterance.voice.name}`);
        } else {
          logger.log(`🎙️ TTS using: ${utterance.voice.name} (${utterance.voice.lang})`);
        }
      }

      utterance.onstart = () => {
        if (voiceSettings.is_parallel) {
          logger.log('🎵 Parallel TTS started');
        } else {
          logger.log('🎙️ TTS started');
        }
      };

      utterance.onend = () => {
        this.emit('speaking', false);
        if (voiceSettings.is_parallel) {
          logger.log('🎵 Parallel TTS completed');
        } else {
          logger.log('🎙️ TTS completed');
        }
      };

      utterance.onerror = (error) => {
        logger.error('Fallback TTS error:', error);
        this.emit('speaking', false);
      };

      // Start speaking immediately
      speechSynthesis.speak(utterance);

    } catch (error) {
      logger.error('Error with fallback TTS:', error);
      this.emit('speaking', false);
    }
  }

  // Method to stop all audio (TTS, sentence queue and streaming)
  stopAllAudio() {
    try {
      // Stop sentence queue audio
      if (this.sentenceAudioQueue && this.sentenceAudioQueue.length > 0) {
        this.sentenceAudioQueue.forEach(item => {
          item.audio.pause();
          item.audio.currentTime = 0;
          URL.revokeObjectURL(item.url);
        });
        this.sentenceAudioQueue = [];
        this.isPlayingSentences = false;
        this.currentSentenceIndex = 0;
        logger.log('🔇 Stopped sentence queue audio');
      }
      
      // Stop TTS audio queue
      if (this.ttsAudioQueue && this.ttsAudioQueue.length > 0) {
        this.ttsAudioQueue.forEach(item => {
          item.audio.pause();
          item.audio.currentTime = 0;
          URL.revokeObjectURL(item.url);
        });
        this.ttsAudioQueue = [];
        this.isPlayingTTSAudio = false;
        logger.log('🔇 Stopped TTS audio');
      }
      
      // Stop fallback TTS
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
        logger.log('🔇 Stopped fallback TTS');
      }

      // Stop streaming audio
      if (this.streamingAudioSource) {
        this.streamingAudioSource.stop();
        this.streamingAudioSource = null;
        logger.log('🔇 Stopped streaming audio');
      }

      // Reset streaming state
      this.isStreamingAudio = false;
      this.streamingAudioChunks = [];

      // Update speaking state
      this.emit('speaking', false);

      logger.log('🔇 All audio stopped');
    } catch (error) {
      logger.error('Error stopping audio:', error);
    }
  }

  // Method to get available voices for user selection
  getAvailableVoices() {
    if (!('speechSynthesis' in window)) {
      return [];
    }

    const voices = speechSynthesis.getVoices();
    return voices.map(voice => ({
      name: voice.name,
      lang: voice.lang,
      gender: voice.name.toLowerCase().includes('female') || 
              voice.name.toLowerCase().includes('karen') ||
              voice.name.toLowerCase().includes('susan') ? 'female' : 'unknown'
    }));
  }
}

export const voiceService = VoiceService;
